<template>
  <div class="catalogo-container">
    <NavbarWorld @cart-click="handleCartRedirect" />

    <!-- HERO CON AZUL ORIGINAL -->
    <section class="catalogo-hero">
      <div class="hero-content">
        <h1>Tienda</h1>
        <p>Busca por nombre, código o categoría</p>

        <div class="search-tools">
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="searchTerm"
              @input="handleSearch"
              placeholder="Ej: Router, Taladro, ABC123..."
              class="search-input"
              autocomplete="off"
              ref="searchInput"
            />
            <button v-if="searchTerm" @click="clearSearch" class="clear-search" type="button">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="category-filter">
            <FilterableSelect
              v-model="selectedCategory"
              :options="categoryOptions"
              placeholder="Todas las categorías"
              label-key="label"
              value-key="value"
              @change="handleCategoryChange"
            />
          </div>

          <div class="currency-toggle premium">
            <button 
              class="currency-btn" 
              :class="{ active: !showBs }"
              @click="showBs = false; toggleCurrency()"
              title="Mostrar precios en Dólares"
            >
              <i class="fas fa-dollar-sign"></i>
              <span>USD</span>
            </button>
            
            <div class="currency-switch-container">
              <button 
                class="switch-button"
                :class="{ 'bs-active': showBs }"
                @click="showBs = !showBs; toggleCurrency()"
                title="Cambiar moneda"
              >
                <div class="switch-slider">
                  <i class="fas fa-exchange-alt"></i>
                </div>
              </button>
              <span class="bcv-badge" v-if="currentRate">
                <i class="fas fa-university"></i>
                BCV: {{ formatNumber(currentRate.precio_usd_bs) }}
              </span>
            </div>
            
            <button 
              class="currency-btn" 
              :class="{ active: showBs }"
              @click="showBs = true; toggleCurrency()"
              title="Mostrar precios en Bolívares"
            >
              <i class="fas fa-bolt"></i>
              <span>Bs</span>
            </button>
          </div>
        </div>

        <div class="payment-type-selector">
          <button 
            class="payment-btn" 
            :class="{ active: paymentType === 'cash' }"
            @click="paymentType = 'cash'"
            title="Precio de contado sin recargo"
          >
            <i class="fas fa-money-bill-wave"></i>
            <span>Contado</span>
          </button>
          <button 
            class="payment-btn credit" 
            :class="{ active: paymentType === 'credit' }"
            @click="paymentType = 'credit'"
            title="Precio de crédito con recargo del 17%"
          >
            <i class="fas fa-credit-card"></i>
            <span>cashea</span>
          </button>
          <div class="payment-info-tooltip">
            <i class="fas fa-info-circle"></i>
            <span class="tooltip-text">El precio de crédito incluye un recargo del 17% sobre el precio de contado</span>
          </div>
        </div>

        <div v-if="searchTerm || selectedCategory !== 'all'" class="search-results-info">
          <span v-if="searchTerm">Buscando: <strong>"{{ searchTerm }}"</strong></span>
          <span v-if="selectedCategory !== 'all'">Categoría: <strong>{{ getCategoryName(selectedCategory) }}</strong></span>
          <span class="results-count">{{ filteredProducts.length }} resultados</span>
        </div>
      </div>
    </section>

    <div v-if="currentRate && showBs" class="rate-banner premium">
      <div class="rate-info">
        <div class="rate-icon">
          <i class="fas fa-chart-line"></i>
        </div>
        <div class="rate-details">
          <span class="rate-label">Tasa BCV del día</span>
          <span class="rate-value"><strong>{{ formatNumber(currentRate.precio_usd_bs) }} Bs/USD</strong></span>
          <span class="rate-date">{{ formatDate(currentRate.fecha) }}</span>
        </div>
        <button class="rate-refresh" @click="loadCurrentRate" title="Actualizar tasa">
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>

    <main class="catalogo-main">
      <div class="container">
        <div class="products-header">
          <div class="header-left">
            <h2>
              Productos 
              <span class="product-count">{{ filteredProducts.length }}</span>
            </h2>
            <div class="price-info" v-if="showBs">
              <i class="fas fa-info-circle"></i>
              <span>Precios convertidos a Bs con tasa BCV</span>
            </div>
          </div>
          
          <div class="header-right">
            <div class="sort-container">
              <label class="sort-label">Ordenado por:</label>
              <div class="sort-wrapper">
                <FilterableSelect
                  v-model="sortBy"
                  :options="sortOptions"
                  placeholder="Seleccionar"
                  label-key="label"
                  value-key="value"
                  @change="sortProducts"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="loading">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Cargando productos...</p>
        </div>

        <div v-else-if="filteredProducts.length === 0" class="no-results">
          <i class="fas fa-search"></i>
          <h3>No se encontraron productos</h3>
          <p v-if="searchTerm">No hay resultados para "{{ searchTerm }}"</p>
          <p v-else-if="selectedCategory !== 'all'">No hay productos en esta categoría</p>
          <p v-else>Intenta con otros términos de búsqueda</p>
          <button v-if="searchTerm || selectedCategory !== 'all'" @click="clearAllFilters" class="btn-clear">
            <i class="fas fa-times"></i> Limpiar filtros
          </button>
        </div>

        <div v-else class="products-grid">
          <div
            v-for="product in paginatedProducts"
            :key="product.idProducto"
            class="product-card"
          >
            <div class="image-wrapper" @click="openImageViewer(product)" title="Ampliar imagen">
              <div class="image-container">
                <img 
                  :src="getProductImage(product.imagen)" 
                  :alt="product.nombre || 'Producto'" 
                  loading="lazy"
                  decoding="async"
                  @error="handleImageError($event, product)"
                />
                <div class="image-placeholder" v-if="!product.imagen || product.imagen === 'no-image'">
                  <i class="fas fa-image"></i>
                  <span>Imagen no disponible</span>
                </div>
              </div>
              <div class="image-overlay">
                <i class="fas fa-search-plus"></i>
              </div>
              <span v-if="product.stock <= 0" class="badge out">Agotado</span>
              <span v-else-if="product.stock <= (product.stock_minimo || 0)" class="badge low">Últimas unidades</span>
            </div>

            <div class="product-info">
              <div class="category-badge" @click="filterByCategory(product.idCategoria)" title="Filtrar por esta categoría">
                {{ product.nombreCategoria || getCategoryName(product.idCategoria) }}
              </div>
              
              <h3 @click="openModal(product)" title="Ver detalles del producto">{{ product.nombre }}</h3>
              <p class="code" @click="openModal(product)" title="Ver detalles del producto">{{ product.codigo }}</p>

              <div class="price-section">
                <div class="price-main">
                  <div class="price-usd" v-if="!showBs">
                    {{ formatUSD(getDisplayPrice(product)) }}
                  </div>
                  <div class="price-bs" v-if="showBs">
                    {{ formatBs(getDisplayPrice(product)) }}
                  </div>
                </div>
                
                <div class="price-original" v-if="paymentType === 'credit'">
                  <span class="original-label">Contado:</span>
                  <span class="original-value">
                    {{ !showBs ? formatUSD(product.precio_venta_usd) : formatBs(product.precio_venta_usd) }}
                  </span>
                </div>
              </div>

              <div class="actions">
                <button class="btn-glow primary" @click.stop="addToCart(product)" title="Agregar al carrito">
                  <span><i class="fas fa-cart-plus"></i> Comprar</span>
                </button>
                <button class="btn-share-card" @click.stop="shareProduct(product)" title="Compartir enlace del producto">
                  <i class="fas fa-share-alt"></i>
                </button>
                <button class="btn-glass" @click.stop="openModal(product)" title="Ver detalles del producto">
                  <i class="fas fa-eye"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="totalPages > 1" class="pagination">
          <button class="btn-glass" @click="prevPage" :disabled="currentPage === 1" title="Página anterior">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info">Página {{ currentPage }} / {{ totalPages }}</span>
          <button class="btn-glass" @click="nextPage" :disabled="currentPage === totalPages" title="Página siguiente">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </main>

    <!-- MODAL DE PRODUCTO -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-card">
        <button class="close-btn" @click="closeModal" title="Cerrar">×</button>

        <div class="modal-content">
          <div class="modal-image" @click="openImageViewer(selectedProduct)" title="Ampliar imagen">
            <div class="image-container">
              <img 
                :src="getProductImage(selectedProduct.imagen)" 
                :alt="selectedProduct.nombre || 'Producto'"
                loading="lazy"
                decoding="async"
                @error="handleImageError($event, selectedProduct)"
              />
              <div class="image-placeholder" v-if="!selectedProduct.imagen || selectedProduct.imagen === 'no-image'">
                <i class="fas fa-image"></i>
                <span>Imagen no disponible</span>
              </div>
            </div>
            <div class="image-zoom-hint">
              <i class="fas fa-search-plus"></i> Click para ampliar
            </div>
          </div>

          <div class="modal-info">
            <div class="modal-header">
              <div class="modal-category">
                {{ selectedProduct.nombreCategoria || getCategoryName(selectedProduct.idCategoria) }}
              </div>
              <h2>{{ selectedProduct.nombre }}</h2>
              <p class="modal-code">Código: {{ selectedProduct.codigo }}</p>
              <p class="modal-brand" v-if="selectedProduct.marca">Marca: {{ selectedProduct.marca }}</p>
              <p class="modal-model" v-if="selectedProduct.modelo">Modelo: {{ selectedProduct.modelo }}</p>
            </div>

            <div class="modal-price-section premium">
              <div class="price-comparison">
                <div class="price-card cash">
                  <div class="price-header">
                    <i class="fas fa-money-bill-wave"></i>
                    <span>Contado</span>
                    <span class="price-badge">Sin recargo</span>
                  </div>
                  <div class="price-amount">
                    <span class="currency-symbol">$</span>
                    <span class="price-value">{{ formatNumber(selectedProduct.precio_venta_usd) }}</span>
                    <span class="currency-label">USD</span>
                  </div>
                  <div class="price-amount-bs" v-if="showBs">
                    <span class="currency-symbol">Bs</span>
                    <span class="price-value">{{ formatBsNumber(selectedProduct.precio_venta_usd) }}</span>
                  </div>
                </div>
                
                <div class="price-divider">
                  <i class="fas fa-plus"></i>
                  <span>+17%</span>
                </div>
                
                <div class="price-card credit">
                  <div class="price-header">
                    <i class="fas fa-credit-card"></i>
                    <span>Crédito</span>
                    <span class="price-badge">Recargo incluido</span>
                  </div>
                  <div class="price-amount">
                    <span class="currency-symbol">$</span>
                    <span class="price-value highlight">{{ formatNumber(getCreditPrice(selectedProduct)) }}</span>
                    <span class="currency-label">USD</span>
                  </div>
                  <div class="price-amount-bs" v-if="showBs">
                    <span class="currency-symbol">Bs</span>
                    <span class="price-value highlight">{{ formatBsNumber(getCreditPrice(selectedProduct)) }}</span>
                  </div>
                </div>
              </div>
              
              <div class="stock-info">
                <i class="fas fa-boxes"></i>
                <span>Stock disponible:</span>
                <strong :class="selectedProduct.stock <= 0 ? 'text-danger' : selectedProduct.stock <= (selectedProduct.stock_minimo || 0) ? 'text-warning' : 'text-success'">
                  {{ selectedProduct.stock <= 0 ? 'Agotado' : `${selectedProduct.stock} unidades` }}
                </strong>
                <span v-if="selectedProduct.stock_minimo" class="stock-minimo">(Mínimo: {{ selectedProduct.stock_minimo }})</span>
              </div>
            </div>

            <div class="modal-desc">
              <h4>Descripción</h4>
              <p>{{ selectedProduct.descripcion || 'Sin descripción disponible.' }}</p>
            </div>

            <div class="modal-garantia" v-if="selectedProduct.garantia_meses">
              <i class="fas fa-shield-alt"></i>
              <span>Garantía: {{ selectedProduct.garantia_meses }} {{ selectedProduct.garantia_meses === 1 ? 'mes' : 'meses' }}</span>
            </div>

            <div class="modal-actions">
              <button 
                class="btn-glow primary big" 
                @click="addToCart(selectedProduct)"
                :disabled="selectedProduct.stock <= 0"
                title="Agregar este producto al carrito"
              >
                <span><i class="fas fa-cart-plus"></i> Agregar al carrito</span>
              </button>
              
              <button 
                class="btn-share" 
                @click="shareProduct(selectedProduct)"
                title="Compartir enlace del producto"
              >
                <i class="fas fa-share-alt"></i>
                <span>Compartir enlace</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- VISOR DE IMAGEN EN GRANDE -->
    <div v-if="showImageViewer" class="image-viewer-overlay" @click.self="closeImageViewer">
      <button class="viewer-close" @click="closeImageViewer" title="Cerrar">
        <i class="fas fa-times"></i>
      </button>
      
      <div class="viewer-container">
        <div class="viewer-image-wrapper">
          <img :src="getProductImage(viewerImage)" :alt="viewerProductName" class="viewer-image" />
        </div>
        <div class="viewer-caption">
          <h3>{{ viewerProductName }}</h3>
          <p>{{ viewerProductCode }}</p>
        </div>
      </div>
      
      <button class="viewer-nav viewer-prev" @click="prevImage" v-if="hasMultipleImages" title="Imagen anterior">
        <i class="fas fa-chevron-left"></i>
      </button>
      <button class="viewer-nav viewer-next" @click="nextImage" v-if="hasMultipleImages" title="Imagen siguiente">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <button class="floating-cart-btn" @click="redirectToCart" title="Ver carrito de compras">
      <i class="fas fa-shopping-cart"></i>
      <span class="cart-count" v-if="cartItemCount > 0">{{ cartItemCount }}</span>
    </button>

    <FooterSection />
  </div>
</template>

<script>
import NavbarWorld from '@/components/NavbarWorld.vue';
import FooterSection from '@/components/FooterSection.vue';
import FilterableSelect from '@/components/FilterableSelect.vue';
import {
  obtenerProductos,
  obtenerUrlImagen,
  ordenarProductos
} from '@/api/productos.js';
import { obtenerCategorias } from '@/api/categorias.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';

export default {
  name: 'CatalogoWiel',
  components: { 
    NavbarWorld, 
    FooterSection,
    FilterableSelect
  },
  
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      products: [],
      filteredProducts: [],
      paginatedProducts: [],
      loading: true,
      
      searchTerm: '',
      selectedCategory: 'all',
      categories: [],
      
      showBs: false,
      currentRate: null,
      
      paymentType: 'cash',
      
      sortBy: 'popular',
      
      currentPage: 1,
      itemsPerPage: 12,
      
      showModal: false,
      selectedProduct: {},
      
      showImageViewer: false,
      viewerImage: '',
      viewerProductName: '',
      viewerProductCode: '',
      viewerImages: [],
      currentImageIndex: 0,
      
      cartItemCount: 0,
      cartItems: JSON.parse(localStorage.getItem('cart') || '[]'),
      
      searchTimeout: null,
      categoryCache: {},
      productCache: {}
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    
    hasMultipleImages() {
      return this.viewerImages.length > 1;
    },

    categoryOptions() {
      const options = [
        { value: 'all', label: 'Todas las categorías' }
      ];
      
      this.categories.forEach(cat => {
        options.push({
          value: String(cat.idCategoria),
          label: cat.nombre
        });
      });
      
      return options;
    },

    sortOptions() {
      return [
        { value: 'popular', label: '⭐ Más popular' },
        { value: 'vendidos', label: '🔥 Más vendidos' },
        { value: 'precioVenta_desc', label: '💰 Menor precio' },
        { value: 'precioVenta', label: '💰 Mayor precio' },
        { value: 'stock_desc', label: '📦 Más stock' },
        { value: 'stock', label: '📦 Menos stock' },
        { value: 'nombre', label: '📝 Nombre (A-Z)' }
      ];
    }
  },

  watch: {
    cartItems: {
      handler(newItems) {
        this.cartItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);
        localStorage.setItem('cart', JSON.stringify(newItems));
      },
      deep: true
    },
    
    '$route.params.id': {
      handler(newId) {
        if (newId && this.products.length > 0) {
          this.openProductFromRoute(newId);
        }
      },
      immediate: false
    }
  },

  async mounted() {
    await this.loadData();
    this.cartItemCount = this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    
    this.adjustItemsPerPage();
    window.addEventListener('resize', this.adjustItemsPerPage);
    window.addEventListener('popstate', this.handlePopState);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.adjustItemsPerPage);
    window.removeEventListener('popstate', this.handlePopState);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },

  methods: {
    handleImageError(event, product) {
      event.target.style.display = 'none';
      const container = event.target.closest('.image-container');
      if (container) {
        const placeholder = container.querySelector('.image-placeholder');
        if (placeholder) {
          placeholder.style.display = 'flex';
        }
      }
      product.imagen = 'no-image';
    },

    getCreditPrice(product) {
      const originalPrice = product.precio_venta_usd || 0;
      return originalPrice * 1.17;
    },
    
    getDisplayPrice(product) {
      if (this.paymentType === 'credit') {
        return this.getCreditPrice(product);
      }
      return product.precio_venta_usd || 0;
    },
    
    adjustItemsPerPage() {
      const width = window.innerWidth;
      if (width < 480) {
        this.itemsPerPage = 6;
      } else if (width < 768) {
        this.itemsPerPage = 8;
      } else if (width < 1024) {
        this.itemsPerPage = 9;
      } else {
        this.itemsPerPage = 12;
      }
      this.updatePagination();
    },

    async loadData() {
      this.loading = true;
      try {
        const [productsResponse, categoriesResponse, rateResponse] = await Promise.all([
          obtenerProductos('activos'),
          obtenerCategorias('activas'),
          obtenerTasaVentaActual().catch(() => ({ success: false, data: null }))
        ]);

        if (productsResponse.success) {
          this.products = productsResponse.data;
          this.filteredProducts = [...this.products];
        }

        if (categoriesResponse.success) {
          this.categories = categoriesResponse.data;
          // Pre-cargar nombres de categorías en caché
          this.categories.forEach(cat => {
            this.categoryCache[cat.idCategoria] = cat.nombre;
          });
        }

        if (rateResponse && rateResponse.success) {
          this.currentRate = rateResponse.data;
        }

        this.sortProducts();
        this.updatePagination();
        
        if (this.id) {
          this.$nextTick(() => {
            this.openProductFromRoute(this.id);
          });
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
      }
      this.loading = false;
    },

    openProductFromRoute(productId) {
      const product = this.products.find(p => p.idProducto == productId);
      if (product) {
        this.$nextTick(() => {
          this.openModal(product);
        });
      } else {
        console.warn('Producto no encontrado con ID:', productId);
        this.showNotification('Producto no encontrado', 'error');
      }
    },

    // BÚSQUEDA OPTIMIZADA - Debounce más rápido
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      // Reducido a 150ms para mayor velocidad
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 150);
    },

    handleCategoryChange() {
      this.applyFilters();
    },

    // FILTRADO OPTIMIZADO - Usando búsqueda binaria y caché
    applyFilters() {
      let filtered = [...this.products];
      
      // Filtro por categoría - optimizado
      if (this.selectedCategory !== 'all') {
        const selectedId = String(this.selectedCategory);
        filtered = filtered.filter(product => 
          String(product.idCategoria) === selectedId
        );
      }
      
      // Búsqueda optimizada
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const term = this.searchTerm.toLowerCase().trim();
        const termLength = term.length;
        
        // Si el término es muy corto, búsqueda más rápida
        if (termLength < 3) {
          filtered = filtered.filter(product => {
            const nombre = (product.nombre || '').toLowerCase();
            const codigo = (product.codigo || '').toLowerCase();
            return nombre.includes(term) || codigo.includes(term);
          });
        } else {
          // Búsqueda completa para términos largos
          filtered = filtered.filter(product => {
            const nombre = (product.nombre || '').toLowerCase();
            const codigo = (product.codigo || '').toLowerCase();
            const categoria = (product.nombreCategoria || this.getCategoryNameFast(product.idCategoria) || '').toLowerCase();
            const marca = (product.marca || '').toLowerCase();
            const modelo = (product.modelo || '').toLowerCase();
            
            return nombre.includes(term) || 
                   codigo.includes(term) || 
                   categoria.includes(term) ||
                   marca.includes(term) ||
                   modelo.includes(term);
          });
        }
        
        // Ordenar resultados por relevancia
        filtered = this.sortSearchResults(filtered, term);
      }
      
      this.filteredProducts = filtered;
      this.currentPage = 1;
      this.sortProducts();
      this.updatePagination();
    },

    // OBTENER NOMBRE DE CATEGORÍA RÁPIDO (con caché)
    getCategoryNameFast(idCategoria) {
      if (this.categoryCache[idCategoria]) {
        return this.categoryCache[idCategoria];
      }
      const category = this.categories.find(cat => cat.idCategoria == idCategoria);
      const name = category ? category.nombre : 'Sin categoría';
      this.categoryCache[idCategoria] = name;
      return name;
    },

    sortSearchResults(products, term) {
      return products.sort((a, b) => {
        const aNombre = (a.nombre || '').toLowerCase();
        const bNombre = (b.nombre || '').toLowerCase();
        const aCodigo = (a.codigo || '').toLowerCase();
        const bCodigo = (b.codigo || '').toLowerCase();
        
        // Priorizar coincidencias exactas
        if (aNombre === term && bNombre !== term) return -1;
        if (aNombre !== term && bNombre === term) return 1;
        if (aCodigo === term && bCodigo !== term) return -1;
        if (aCodigo !== term && bCodigo === term) return 1;
        
        // Priorizar palabras que empiezan con el término
        const aStartsWith = aNombre.startsWith(term);
        const bStartsWith = bNombre.startsWith(term);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        
        return aNombre.localeCompare(bNombre);
      });
    },

    clearSearch() {
      this.searchTerm = '';
      this.applyFilters();
      // Enfocar el input de búsqueda
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    clearAllFilters() {
      this.searchTerm = '';
      this.selectedCategory = 'all';
      this.applyFilters();
    },

    filterByCategory(categoryId) {
      this.selectedCategory = String(categoryId);
      this.applyFilters();
    },

    // ORDENAMIENTO OPTIMIZADO
    sortProducts() {
      if (this.sortBy === 'popular') {
        // Mantener orden original o por nombre
        this.filteredProducts = ordenarProductos(this.filteredProducts, 'nombre');
      } else if (this.sortBy === 'vendidos') {
        this.filteredProducts = ordenarProductos(this.filteredProducts, 'nombre');
      } else {
        this.filteredProducts = ordenarProductos(this.filteredProducts, this.sortBy);
      }
      this.updatePagination();
    },

    updatePagination() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      this.paginatedProducts = this.filteredProducts.slice(start, start + this.itemsPerPage);
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.updatePagination();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.updatePagination();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    openImageViewer(product) {
      if (!product.imagen || product.imagen === 'no-image') {
        this.showNotification('Este producto no tiene imagen disponible', 'error');
        return;
      }
      
      this.viewerImage = product.imagen;
      this.viewerProductName = product.nombre;
      this.viewerProductCode = product.codigo;
      this.viewerImages = [product.imagen];
      this.currentImageIndex = 0;
      this.showImageViewer = true;
      document.body.style.overflow = 'hidden';
    },

    closeImageViewer() {
      this.showImageViewer = false;
      document.body.style.overflow = 'auto';
    },

    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
        this.viewerImage = this.viewerImages[this.currentImageIndex];
      }
    },

    nextImage() {
      if (this.currentImageIndex < this.viewerImages.length - 1) {
        this.currentImageIndex++;
        this.viewerImage = this.viewerImages[this.currentImageIndex];
      }
    },

    // FORMATOS OPTIMIZADOS - usando caché de formato
    formatUSD(value) {
      const n = Number(value || 0);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n);
    },

    formatBs(value) {
      if (!this.currentRate) return 'Bs 0,00';
      const n = Number(value || 0);
      const bsAmount = n * this.currentRate.precio_usd_bs;
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'VES',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(bsAmount).replace('VES', 'Bs');
    },

    formatBsNumber(value) {
      if (!this.currentRate) return '0,00';
      const n = Number(value || 0);
      const bsAmount = n * this.currentRate.precio_usd_bs;
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(bsAmount);
    },

    formatNumber(value) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-VE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },

    toggleCurrency() {
      if (this.showBs && !this.currentRate) {
        this.loadCurrentRate();
      }
    },

    async loadCurrentRate() {
      const response = await obtenerTasaVentaActual();
      if (response.success) {
        this.currentRate = response.data;
      }
    },

    getProductImage(img) {
      try {
        if (!img || img === 'no-image') {
          return null;
        }
        return obtenerUrlImagen(img);
      } catch {
        return null;
      }
    },

    getCategoryName(idCategoria) {
      return this.getCategoryNameFast(idCategoria);
    },

    openModal(product) {
      this.selectedProduct = product;
      this.showModal = true;
      document.body.style.overflow = 'hidden';
      
      if (product && product.idProducto) {
        const newUrl = `/producto/${product.idProducto}`;
        if (this.$route.path !== newUrl) {
          this.$router.push({ 
            name: 'producto-detalle', 
            params: { id: String(product.idProducto) } 
          }).catch(() => {});
        }
      }
    },

    closeModal() {
      this.showModal = false;
      this.selectedProduct = {};
      document.body.style.overflow = 'auto';
      
      if (this.$route.name === 'producto-detalle') {
        this.$router.push({ name: 'catalogo' }).catch(() => {});
      }
    },

    async shareProduct(product) {
      const shareUrl = `${window.location.origin}/producto/${product.idProducto}`;
      const shareText = `🔌 ${product.nombre}\n📦 Código: ${product.codigo}\n💰 Precio Contado: $${this.formatNumber(product.precio_venta_usd)}\n💳 Precio Crédito: $${this.formatNumber(this.getCreditPrice(product))}\n🔗 Ver más: ${shareUrl}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: product.nombre,
            text: shareText,
            url: shareUrl
          });
          this.showNotification('¡Producto compartido exitosamente!', 'success');
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.copyToClipboard(shareUrl);
          }
        }
      } else {
        this.copyToClipboard(shareUrl);
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.showNotification('✅ Enlace copiado al portapapeles', 'success');
      } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.showNotification('✅ Enlace copiado al portapapeles', 'success');
        } catch (err) {
          this.showNotification('❌ No se pudo copiar el enlace', 'error');
        }
        document.body.removeChild(textArea);
      }
    },

    handlePopState() {
      if (this.showModal) {
        this.closeModal();
      }
    },

    addToCart(product) {
      if (product.stock <= 0) {
        this.showNotification('Producto agotado', 'error');
        return;
      }
      
      const existingItem = this.cartItems.find(item => item.id === product.idProducto);
      const priceToAdd = this.getDisplayPrice(product);
      
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        this.cartItems.push({
          id: product.idProducto,
          nombre: product.nombre,
          codigo: product.codigo,
          precio: priceToAdd,
          precioOriginal: product.precio_venta_usd,
          paymentType: this.paymentType,
          imagen: product.imagen,
          quantity: 1
        });
      }
      
      const paymentText = this.paymentType === 'credit' ? '(Precio Crédito +17%)' : '(Precio Contado)';
      this.showNotification(`${product.nombre} se agregó al carrito ${paymentText}`, 'success');
    },

    showNotification(message, type = 'success') {
      const notification = document.createElement('div');
      notification.className = `notification ${type}`;
      notification.innerHTML = `
        <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i>
        <span>${message}</span>
      `;
      
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === 'success' ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #ef4444, #dc2626)'};
        color: white;
        padding: 12px 20px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        gap: 10px;
        box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        z-index: 99999;
        animation: slideIn 0.3s ease;
        max-width: 90%;
        font-size: ${window.innerWidth < 768 ? '14px' : '16px'};
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => {
          if (notification.parentNode) {
            document.body.removeChild(notification);
          }
        }, 300);
      }, 3000);
    },

    handleCartRedirect() {
      this.redirectToCart();
    },

    redirectToCart() {
      this.$router.push('/carrito');
    }
  }
};
</script>

<style scoped>
/* ===== VARIABLES GLOBALES ===== */
:root {
  --primary-blue: #1e3a8a;
  --secondary-blue: #2563eb;
  --light-blue: #3b82f6;
  --dark-blue: #0f172a;
  --success: #22c55e;
  --warning: #f59e0b;
  --danger: #ef4444;
  --text-dark: #0f172a;
  --text-light: #64748b;
  --white: #ffffff;
  --gray-bg: #f8fafc;
  --gradient-blue: linear-gradient(135deg, #2563eb, #3b82f6, #60a5fa);
  --gradient-gold: linear-gradient(135deg, #f59e0b, #fbbf24);
  --gradient-credit: linear-gradient(135deg, #8b5cf6, #a78bfa, #c084fc);
  --gradient-venezuela: linear-gradient(135deg, #dc2626, #2563eb, #fbbf24);
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.catalogo-container {
  min-height: 100vh;
  background-color: #030812;
  background-image:
    radial-gradient(circle at 50% 12%, #0a1c2e 0%, #030812 75%),
    linear-gradient(rgba(0, 229, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 229, 255, 0.02) 1px, transparent 1px);
  background-size: 100% 100%, 45px 45px, 45px 45px;
  font-family: 'Poppins', sans-serif;
  width: 100%;
  overflow-x: hidden;
  color: #f8fafc;
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

@media (min-width: 640px) {
  .container {
    padding: 0 1.5rem;
  }
}

@media (min-width: 1024px) {
  .container {
    padding: 0 2rem;
  }
}

.catalogo-hero {
  background: radial-gradient(circle at 50% 40%, #0a1c2e 0%, #030812 85%);
  color: white;
  padding: 7rem 1rem 2.5rem 1rem;
  text-align: center;
  width: 100%;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(0, 229, 255, 0.15);
}

.catalogo-hero::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 229, 255, 0.08) 0%, transparent 70%);
  animation: rotate 30s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (min-width: 768px) {
  .catalogo-hero {
    padding: 8rem 1rem 2.5rem 1rem;
  }
}

@media (min-width: 1024px) {
  .catalogo-hero {
    padding: 9.5rem 1rem 3rem 1rem;
  }
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 2;
}

.hero-content h1 {
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #7ef0ff 50%, #00e5ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  letter-spacing: -0.5px;
}

@media (min-width: 768px) {
  .hero-content h1 {
    font-size: 2.5rem;
  }
}

.hero-content > p {
  color: #94a3b8;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

@media (min-width: 768px) {
  .hero-content > p {
    font-size: 1rem;
  }
}

.search-tools {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .search-tools {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
  }
}

.search-box {
  width: 100%;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 999px;
  padding: 0.7rem 1.2rem;
  display: flex;
  gap: 0.6rem;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  transition: all 0.3s ease;
  border: 1.5px solid rgba(0, 229, 255, 0.3);
}

@media (min-width: 768px) {
  .search-box {
    flex: 1;
    min-width: 250px;
    max-width: 400px;
    padding: 0.8rem 1.2rem;
  }
}

.search-box:focus-within {
  border-color: #00e5ff;
  box-shadow: 0 0 25px rgba(0, 229, 255, 0.35);
  transform: scale(1.02);
}

.search-icon {
  color: #00e5ff;
  font-size: 0.9rem;
}

@media (min-width: 768px) {
  .search-icon {
    font-size: 1rem;
  }
}

.search-input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 0.9rem;
  background: transparent;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
}

@media (min-width: 768px) {
  .search-input {
    font-size: 0.95rem;
  }
}

.search-input::placeholder {
  color: #64748b;
}

.clear-search {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.8rem;
}

.clear-search:hover {
  background: rgba(0, 229, 255, 0.15);
  color: #00e5ff;
  transform: rotate(90deg);
}

.category-filter {
  width: 100%;
}

@media (min-width: 768px) {
  .category-filter {
    width: auto;
    min-width: 220px;
  }
}

.category-filter .filterable-select {
  background: rgba(15, 23, 42, 0.85);
}

.category-filter .select-input-wrapper {
  background: rgba(15, 23, 42, 0.85);
  border-color: rgba(0, 229, 255, 0.3);
}

.category-filter .select-input {
  color: white;
}

.category-filter .select-input::placeholder {
  color: #94a3b8;
}

.category-filter .selected-label {
  color: #7ef0ff;
}

.category-filter .select-dropdown {
  background: #0f172a;
  border-color: rgba(0, 229, 255, 0.35);
}

.category-filter .dropdown-option {
  color: #e2e8f0;
}

.category-filter .dropdown-option:hover,
.category-filter .dropdown-option.highlighted {
  background: rgba(0, 229, 255, 0.15);
  color: #00e5ff;
}

.category-filter .dropdown-option.active {
  background: rgba(0, 229, 255, 0.25);
  color: #00e5ff;
}

.category-filter .select-arrow {
  color: #00e5ff;
}

.currency-toggle.premium {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  padding: 0.4rem;
  border-radius: 50px;
  border: 1px solid rgba(0, 229, 255, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  width: 100%;
  justify-content: center;
}

@media (min-width: 768px) {
  .currency-toggle.premium {
    width: auto;
    min-width: 380px;
    padding: 0.4rem 0.5rem;
  }
}

.currency-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 40px;
  background: transparent;
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

@media (min-width: 768px) {
  .currency-btn {
    padding: 0.6rem 1.5rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }
}

.currency-btn i {
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.currency-btn.active {
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  color: #030812;
  box-shadow: 0 0 18px rgba(0, 229, 255, 0.45);
  transform: scale(1.05);
}

.currency-btn.active i {
  transform: scale(1.1);
}

.currency-btn:not(.active):hover {
  color: white;
  background: rgba(255,255,255,0.1);
}

.currency-switch-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  position: relative;
}

.switch-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #334155, #1e293b);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  position: relative;
  overflow: hidden;
}

@media (min-width: 768px) {
  .switch-button {
    width: 52px;
    height: 52px;
  }
}

.switch-button::before {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  z-index: 1;
}

.switch-slider {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.switch-button i {
  font-size: 1.1rem;
  color: #fbbf24;
  transition: all 0.3s ease;
  position: relative;
  z-index: 3;
}

@media (min-width: 768px) {
  .switch-button i {
    font-size: 1.3rem;
  }
}

.switch-button.bs-active {
  background: var(--gradient-gold);
  animation: pulse 2s infinite;
}

.switch-button.bs-active i {
  color: #1e3a8a;
  transform: rotate(360deg);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
  }
}

.bcv-badge {
  font-size: 0.7rem;
  color: #fbbf24;
  background: rgba(0,0,0,0.3);
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  border: 1px solid rgba(245, 158, 11, 0.3);
  margin-top: 0.1rem;
}

@media (min-width: 768px) {
  .bcv-badge {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
    position: absolute;
    top: -25px;
    margin-top: 0;
  }
}

.bcv-badge i {
  font-size: 0.6rem;
  color: #fbbf24;
}

.payment-type-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.8rem 1.5rem;
  border-radius: 60px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

@media (min-width: 768px) {
  .payment-type-selector {
    gap: 1.5rem;
    padding: 0.8rem 2rem;
  }
}

.payment-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 40px;
  background: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

@media (min-width: 768px) {
  .payment-btn {
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    gap: 0.8rem;
  }
}

.payment-btn i {
  font-size: 1rem;
}

.payment-btn.active {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4);
  transform: scale(1.05);
}

.payment-btn.credit.active {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4);
}

.payment-btn:not(.active):hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.payment-info-tooltip {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
  cursor: help;
}

.payment-info-tooltip i {
  color: #94a3b8;
  font-size: 1rem;
  transition: color 0.3s ease;
}

.payment-info-tooltip:hover i {
  color: #fbbf24;
}

.tooltip-text {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
  margin-bottom: 10px;
  z-index: 100;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
}

.tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: #1e293b transparent transparent transparent;
}

.payment-info-tooltip:hover .tooltip-text {
  opacity: 1;
  visibility: visible;
}

@media (max-width: 480px) {
  .tooltip-text {
    white-space: normal;
    width: 200px;
    text-align: center;
  }
}

.search-results-info {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: #e2e8f0;
  font-size: 0.85rem;
  background: rgba(0,0,0,0.2);
  padding: 0.5rem 1rem;
  border-radius: 30px;
  backdrop-filter: blur(5px);
  display: inline-flex;
  margin-left: auto;
  margin-right: auto;
  width: fit-content;
}

@media (min-width: 480px) {
  .search-results-info {
    flex-direction: row;
    gap: 1rem;
    font-size: 0.9rem;
  }
}

.results-count {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  padding: 0.2rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
}

.rate-banner.premium {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  padding: 0.8rem 1rem;
  border-bottom: 2px solid #3b82f6;
  position: relative;
  overflow: hidden;
}

.rate-banner.premium::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59,130,246,0.2), transparent);
  animation: shine 3s infinite;
}

@keyframes shine {
  to {
    left: 100%;
  }
}

.rate-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.rate-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.rate-icon i {
  color: white;
  font-size: 1.2rem;
}

.rate-details {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  background: rgba(255,255,255,0.1);
  padding: 0.5rem 1rem;
  border-radius: 30px;
  backdrop-filter: blur(5px);
}

.rate-label {
  color: #94a3b8;
  font-size: 0.85rem;
}

.rate-value {
  color: #fbbf24;
  font-size: 1.1rem;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(245,158,11,0.3);
}

.rate-date {
  color: #64748b;
  font-size: 0.8rem;
  padding-left: 0.5rem;
  border-left: 1px solid #334155;
}

.rate-refresh {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.1);
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.rate-refresh:hover {
  background: #3b82f6;
  color: white;
  transform: rotate(180deg);
}

/* ===== ENCABEZADO DE PRODUCTOS ===== */
.products-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  margin: 1.5rem 0;
}

@media (min-width: 640px) {
  .products-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-left h2 {
  font-size: 1.5rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
}

@media (min-width: 768px) {
  .header-left h2 {
    font-size: 1.8rem;
  }
}

.product-count {
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  color: #030812;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 700;
}

.price-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7ef0ff;
  font-size: 0.8rem;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(0, 229, 255, 0.25);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
}

@media (min-width: 768px) {
  .price-info {
    font-size: 0.9rem;
  }
}

.price-info i {
  color: #00e5ff;
}

/* ===== SELECT DE ORDENAMIENTO CON TÍTULO ===== */
.header-right {
  width: 100%;
}

@media (min-width: 640px) {
  .header-right {
    width: auto;
    min-width: 250px;
  }
}

.sort-container {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  width: 100%;
}

.sort-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (min-width: 768px) {
  .sort-label {
    font-size: 0.8rem;
  }
}

.sort-wrapper {
  position: relative;
  width: 100%;
}

.sort-wrapper .filterable-select {
  width: 100%;
}

.sort-wrapper .select-input-wrapper {
  background: rgba(15, 23, 42, 0.85);
  border: 1.5px solid rgba(0, 229, 255, 0.3);
  border-radius: 12px;
  padding-left: 12px;
}

.sort-wrapper .select-input {
  color: #ffffff;
  background: transparent;
  padding-left: 12px;
  font-weight: 500;
  font-size: 0.85rem;
}

@media (min-width: 768px) {
  .sort-wrapper .select-input {
    font-size: 0.9rem;
  }
}

.sort-wrapper .select-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.sort-wrapper .selected-label {
  left: 12px;
  color: #7ef0ff;
  font-weight: 600;
}

.sort-wrapper .select-dropdown {
  background: #0f172a;
  border: 1px solid rgba(0, 229, 255, 0.35);
  border-radius: 12px;
  margin-top: 4px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.8);
}

.sort-wrapper .dropdown-option {
  color: #cbd5e1;
  font-weight: 500;
  padding: 0.7rem 1rem;
  font-size: 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.sort-wrapper .dropdown-option:last-child {
  border-bottom: none;
}

.sort-wrapper .dropdown-option:hover,
.sort-wrapper .dropdown-option.highlighted {
  background: rgba(0, 229, 255, 0.15);
  color: #00e5ff;
}

.sort-wrapper .dropdown-option.active {
  background: rgba(0, 229, 255, 0.25);
  color: #00e5ff;
  font-weight: 600;
}

.sort-wrapper .dropdown-option i {
  color: #00e5ff;
  font-size: 0.9rem;
}

.sort-wrapper .select-arrow {
  color: #00e5ff;
  font-size: 0.8rem;
}

/* Responsive */
@media (max-width: 640px) {
  .header-right {
    width: 100%;
    min-width: unset;
  }
  
  .sort-container {
    width: 100%;
  }
  
  .sort-wrapper .filterable-select {
    min-width: unset;
  }
}

.products-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;
}

@media (min-width: 480px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (min-width: 768px) {
  .products-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.2rem;
  }
}

@media (min-width: 1024px) {
  .products-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
}

.product-card {
  background: rgba(15, 23, 42, 0.82);
  border: 1px solid rgba(0, 229, 255, 0.2);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  will-change: transform;
}

@media (min-width: 1024px) {
  .product-card {
    border-radius: 18px;
    box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  }
}

.product-card:hover {
  transform: translateY(-5px);
  border-color: #00e5ff;
  box-shadow: 0 15px 35px rgba(0, 229, 255, 0.25);
}

@media (min-width: 1024px) {
  .product-card:hover {
    transform: translateY(-8px);
  }
}

.product-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #00e5ff, #1a73e8, #7ef0ff);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.product-card:hover::after {
  transform: scaleX(1);
}

.image-wrapper {
  position: relative;
  height: 160px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(2, 6, 23, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(0, 229, 255, 0.12);
}

@media (min-width: 480px) {
  .image-wrapper {
    height: 180px;
  }
}

@media (min-width: 768px) {
  .image-wrapper {
    height: 200px;
  }
}

@media (min-width: 1024px) {
  .image-wrapper {
    height: 220px;
  }
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.image-wrapper img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.5s ease;
  display: block;
}

.product-card:hover .image-wrapper img {
  transform: scale(1.05);
}

.image-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.75);
  color: #64748b;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
}

.image-placeholder i {
  font-size: 3rem;
  color: #cbd5e1;
}

.image-placeholder span {
  background: rgba(15, 23, 42, 0.9);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  color: #7ef0ff;
  border: 1px solid rgba(0, 229, 255, 0.2);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-overlay i {
  color: white;
  font-size: 1.5rem;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(0.5);
  transition: transform 0.3s ease;
}

@media (min-width: 768px) {
  .image-overlay i {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.image-wrapper:hover .image-overlay i {
  transform: scale(1);
}

.badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 600;
  color: white;
  z-index: 2;
}

@media (min-width: 768px) {
  .badge {
    top: 10px;
    right: 10px;
    padding: 4px 10px;
    font-size: 0.75rem;
  }
}

.badge.out {
  background: var(--danger);
  animation: pulse 2s infinite;
}

.badge.low {
  background: var(--warning);
}

.category-badge {
  display: inline-block;
  background: rgba(0, 229, 255, 0.12);
  color: #7ef0ff;
  border: 1px solid rgba(0, 229, 255, 0.3);
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

@media (min-width: 768px) {
  .category-badge {
    padding: 4px 10px;
    font-size: 0.75rem;
    border-radius: 12px;
    margin-bottom: 0.8rem;
  }
}

.category-badge:hover {
  background: #00e5ff;
  color: #030812;
  box-shadow: 0 0 12px rgba(0, 229, 255, 0.5);
  transform: scale(1.05);
}

.product-info {
  padding: 0.8rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .product-info {
    padding: 1.2rem;
  }
}

.product-info h3 {
  font-size: 0.9rem;
  color: #ffffff;
  margin: 0.2rem 0;
  line-height: 1.3;
  flex: 1;
  cursor: pointer;
  transition: color 0.2s ease;
}

@media (min-width: 768px) {
  .product-info h3 {
    font-size: 1.1rem;
    margin: 0.3rem 0;
  }
}

.product-info h3:hover {
  color: #00e5ff;
}

.code {
  color: #64748b;
  font-size: 0.7rem;
  margin: 0.2rem 0 0.5rem;
  cursor: pointer;
}

@media (min-width: 768px) {
  .code {
    font-size: 0.85rem;
    margin: 0.3rem 0 1rem;
  }
}

.price-section {
  margin: 0.5rem 0;
}

@media (min-width: 768px) {
  .price-section {
    margin: 1rem 0;
  }
}

.price-main .price-usd,
.price-main .price-bs {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
}

@media (min-width: 768px) {
  .price-main .price-usd,
  .price-main .price-bs {
    font-size: 1.5rem;
  }
}

.price-main .price-usd {
  color: #10b981;
}

.price-main .price-bs {
  color: #00e5ff;
  animation: glow 2s ease-in-out infinite;
}

@keyframes glow {
  0%, 100% { text-shadow: 0 0 5px rgba(0, 229, 255, 0.4); }
  50% { text-shadow: 0 0 15px rgba(0, 229, 255, 0.8); }
}

.price-original {
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  width: fit-content;
  margin-top: 0.2rem;
}

@media (min-width: 768px) {
  .price-original {
    font-size: 0.85rem;
    padding: 0.3rem 0.7rem;
  }
}

.original-label {
  color: #475569;
  font-weight: 500;
}

.original-value {
  color: #94a3b8;
  text-decoration: line-through;
  font-weight: 500;
}

.actions {
  display: flex;
  gap: 0.3rem;
  margin-top: 0.5rem;
}

@media (min-width: 768px) {
  .actions {
    gap: 0.5rem;
    margin-top: 1rem;
  }
}

.btn-glow {
  position: relative;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  cursor: pointer;
  overflow: hidden;
  font-weight: 700;
  color: #030812;
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  transition: all 0.3s ease;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  box-shadow: 0 4px 15px rgba(0, 229, 255, 0.35);
}

@media (min-width: 768px) {
  .btn-glow {
    border-radius: 12px;
    padding: 0.8rem 1.2rem;
    gap: 0.5rem;
    font-size: 0.9rem;
  }
}

.btn-glow i {
  font-size: 0.8rem;
}

@media (min-width: 768px) {
  .btn-glow i {
    font-size: 1rem;
  }
}

.btn-glow::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s ease;
}

.btn-glow:hover::before {
  left: 100%;
}

.btn-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 25px rgba(0, 229, 255, 0.65);
}

/* ===== BOTÓN COMPARTIR EN TARJETA ===== */
.btn-share-card {
  border-radius: 8px;
  padding: 0.5rem;
  border: 1px solid rgba(0, 229, 255, 0.3);
  background: rgba(0, 229, 255, 0.15);
  color: #00e5ff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
}

@media (min-width: 768px) {
  .btn-share-card {
    border-radius: 12px;
    padding: 0.8rem;
    min-width: 44px;
  }
}

.btn-share-card i {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

@media (min-width: 768px) {
  .btn-share-card i {
    font-size: 1rem;
  }
}

.btn-share-card:hover {
  transform: scale(1.1) rotate(360deg);
  background: #00e5ff;
  color: #030812;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.6);
}

.btn-glass {
  border-radius: 8px;
  padding: 0.5rem;
  border: 1px solid rgba(0, 229, 255, 0.25);
  background: rgba(15, 23, 42, 0.6);
  color: #7ef0ff;
  backdrop-filter: blur(6px);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
}

@media (min-width: 768px) {
  .btn-glass {
    border-radius: 12px;
    padding: 0.8rem;
    min-width: 44px;
  }
}

.btn-glass i {
  font-size: 0.8rem;
}

@media (min-width: 768px) {
  .btn-glass i {
    font-size: 1rem;
  }
}

.btn-glass:hover {
  background: rgba(0, 229, 255, 0.25);
  color: #00e5ff;
  transform: scale(1.1);
  box-shadow: 0 0 12px rgba(0, 229, 255, 0.4);
}

.btn-glow.big {
  width: auto;
  flex: 1;
  min-width: 200px;
  margin-top: 0;
  padding: 0.8rem;
  font-size: 0.9rem;
}

@media (min-width: 768px) {
  .btn-glow.big {
    padding: 1rem;
    font-size: 1rem;
  }
}

.btn-glow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Estilos mejorados para el botón de compartir en el modal */
.modal-actions .btn-share {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
  min-width: 140px;
  flex: 1;
}

.modal-actions .btn-share:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
}

.modal-actions .btn-share i {
  font-size: 1.1rem;
  transition: transform 0.4s ease;
}

.modal-actions .btn-share:hover i {
  transform: rotate(360deg) scale(1.1);
}

@media (min-width: 768px) {
  .modal-actions .btn-share {
    padding: 1rem 2rem;
    font-size: 1rem;
    min-width: 160px;
  }
  
  .modal-actions .btn-share i {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .modal-actions .btn-share {
    width: 100%;
  }
}

/* Tooltips personalizados para todos los botones con título */
[title] {
  position: relative;
}

[title]:hover::after {
  content: attr(title);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #0f172a;
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  white-space: nowrap;
  z-index: 100;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  pointer-events: none;
  font-weight: 500;
}

[title]:hover::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #0f172a;
  z-index: 100;
  pointer-events: none;
}

/* Ajuste para tooltips en botones pequeños */
.btn-glass[title]:hover::after,
.btn-share-card[title]:hover::after,
.btn-share[title]:hover::after {
  font-size: 0.65rem;
  padding: 3px 8px;
}

/* Ajuste para tooltips en elementos de imagen */
.image-wrapper[title]:hover::after,
.modal-image[title]:hover::after {
  font-size: 0.65rem;
  padding: 3px 8px;
}

.modal-actions {
  display: flex;
  gap: 0.8rem;
  margin-top: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .modal-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .modal-actions .btn-glow.big,
  .modal-actions .btn-share {
    width: 100%;
  }
}

.modal-price-section.premium {
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(0, 229, 255, 0.25);
  border-radius: 20px;
  padding: 1rem;
  margin: 1rem 0;
}

@media (min-width: 768px) {
  .modal-price-section.premium {
    padding: 1.5rem;
  }
}

.price-comparison {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .price-comparison {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.price-card {
  flex: 1;
  background: #0d1e38;
  border-radius: 16px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 229, 255, 0.25);
}

.price-card.cash {
  border-left: 4px solid #22c55e;
}

.price-card.credit {
  border-left: 4px solid #8b5cf6;
  background: #0d1e38;
}

.price-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
}

.price-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
  font-weight: 600;
  color: #334155;
}

.price-header i {
  font-size: 1.2rem;
}

.price-card.cash .price-header i {
  color: #22c55e;
}

.price-card.credit .price-header i {
  color: #8b5cf6;
}

.price-badge {
  font-size: 0.7rem;
  background: #f1f5f9;
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  color: #475569;
}

.price-amount {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.2rem;
  margin: 0.5rem 0;
}

.currency-symbol {
  font-size: 1rem;
  font-weight: 600;
  color: #64748b;
}

.price-value {
  font-size: 1.8rem;
  font-weight: 800;
  color: #ffffff;
}

.price-value.highlight {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.currency-label {
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
}

.price-amount-bs {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.2rem;
  margin-top: 0.3rem;
  font-size: 0.9rem;
}

.price-amount-bs .currency-symbol {
  font-size: 0.8rem;
}

.price-amount-bs .price-value {
  font-size: 1.2rem;
}

.price-divider {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  color: #8b5cf6;
  font-weight: bold;
  font-size: 0.9rem;
}

@media (min-width: 640px) {
  .price-divider {
    flex-direction: row;
    gap: 0.5rem;
    padding: 0 1rem;
  }
}

.price-divider i {
  font-size: 1rem;
}

.stock-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
  font-size: 0.9rem;
  flex-wrap: wrap;
}

.stock-minimo {
  font-size: 0.75rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.1rem 0.4rem;
  border-radius: 10px;
}

.text-success {
  color: #22c55e;
}

.text-warning {
  color: #f59e0b;
}

.text-danger {
  color: #ef4444;
}

.modal-garantia {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
  padding: 0.5rem;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(0, 229, 255, 0.2);
  border-radius: 12px;
  color: #7ef0ff;
  font-size: 0.9rem;
}

.modal-garantia i {
  color: #f59e0b;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
  margin: 2rem 0;
  flex-wrap: wrap;
}

@media (min-width: 768px) {
  .pagination {
    gap: 1.5rem;
    margin: 3rem 0;
  }
}

.page-info {
  color: #7ef0ff;
  font-weight: 600;
  font-size: 0.9rem;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(0, 229, 255, 0.25);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
}

@media (min-width: 768px) {
  .page-info {
    font-size: 1rem;
  }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 0.5rem;
  backdrop-filter: blur(5px);
}

.modal-card {
  background: #0b192e;
  border: 1.5px solid rgba(0, 229, 255, 0.35);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85);
  color: #ffffff;
  border-radius: 20px;
  max-width: 1000px;
  width: 100%;
  max-height: 98vh;
  overflow-y: auto;
  position: relative;
  animation: modalPop 0.3s ease;
}

@media (min-width: 768px) {
  .modal-card {
    border-radius: 24px;
    max-height: 90vh;
  }
}

.modal-content {
  display: flex;
  flex-direction: column;
  padding: 1rem;
}

@media (min-width: 768px) {
  .modal-content {
    flex-direction: row;
    gap: 2rem;
    padding: 2rem;
  }
}

.modal-image {
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  cursor: pointer;
  background: rgba(2, 6, 23, 0.75);
  border: 1px solid rgba(0, 229, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 768px) {
  .modal-image {
    flex: 1;
    height: auto;
    min-height: 300px;
    margin-bottom: 0;
    border-radius: 16px;
  }
}

.modal-image .image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.modal-image img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.modal-image .image-placeholder i {
  font-size: 4rem;
}

@media (min-width: 768px) {
  .modal-image .image-placeholder i {
    font-size: 6rem;
  }
}

.image-zoom-hint {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0,0,0,0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(4px);
}

.modal-info {
  flex: 1;
}

.modal-header h2 {
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 0.3rem;
}

@media (min-width: 768px) {
  .modal-header h2 {
    font-size: 1.8rem;
  }
}

.modal-category {
  display: inline-block;
  background: rgba(0, 229, 255, 0.15);
  color: #7ef0ff;
  border: 1px solid rgba(0, 229, 255, 0.3);
  padding: 0.2rem 0.8rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.modal-code {
  color: #64748b;
  font-size: 0.8rem;
  margin: 0.3rem 0;
}

.modal-brand, .modal-model {
  color: #475569;
  font-size: 0.8rem;
  margin: 0.2rem 0;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
  font-size: 1.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
}

.close-btn:hover {
  background: #ef4444;
  color: white;
  transform: rotate(90deg);
}

.image-viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.98);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.viewer-close {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.2);
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .viewer-close {
    top: 30px;
    right: 30px;
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
}

.viewer-close:hover {
  background: #ef4444;
  transform: rotate(90deg);
}

.viewer-container {
  max-width: 100%;
  max-height: 90vh;
  position: relative;
}

.viewer-image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-image {
  max-width: 100%;
  max-height: 80vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}

.viewer-caption {
  position: absolute;
  bottom: -50px;
  left: 0;
  right: 0;
  text-align: center;
  color: white;
  background: rgba(0,0,0,0.5);
  padding: 0.8rem;
  border-radius: 8px;
  font-size: 0.9rem;
  backdrop-filter: blur(5px);
}

@media (min-width: 768px) {
  .viewer-caption {
    bottom: -60px;
    padding: 1rem;
    font-size: 1rem;
  }
}

.viewer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.2);
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .viewer-nav {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
}

.viewer-nav:hover {
  background: rgba(59,130,246,0.8);
  transform: translateY(-50%) scale(1.1);
}

.viewer-prev {
  left: 10px;
}

.viewer-next {
  right: 10px;
}

@media (min-width: 768px) {
  .viewer-prev {
    left: 30px;
  }
  
  .viewer-next {
    right: 30px;
  }
}

.floating-cart-btn {
  position: fixed;
  bottom: 28px;
  left: 24px;
  right: auto;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  border: 2px solid rgba(0, 229, 255, 0.6);
  color: #030812;
  font-size: 1.35rem;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(0, 229, 255, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.3s ease;
  animation: bounce 2.5s infinite;
}

@media (max-width: 520px) {
  .floating-cart-btn {
    bottom: calc(52px + env(safe-area-inset-bottom, 12px));
    left: 16px;
    right: auto;
    width: 52px;
    height: 52px;
    font-size: 1.2rem;
  }
}

@media (min-width: 768px) {
  .floating-cart-btn {
    bottom: 32px;
    left: 28px;
    right: auto;
    width: 62px;
    height: 62px;
    font-size: 1.45rem;
  }
}

.floating-cart-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 12px 30px rgba(0, 229, 255, 0.65);
}

.cart-count {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  animation: pulse 2s infinite;
}

@media (min-width: 768px) {
  .cart-count {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
}

.loading, .no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(0, 229, 255, 0.25);
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.6);
  color: #f8fafc;
}

@media (min-width: 768px) {
  .loading, .no-results {
    padding: 4rem 1rem;
  }
}

.loading i {
  font-size: 2rem;
  color: #3b82f6;
  margin-bottom: 1rem;
}

@media (min-width: 768px) {
  .loading i {
    font-size: 3rem;
  }
}

.btn-clear {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  border: 2px solid #3b82f6;
  background: transparent;
  color: #3b82f6;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

@media (min-width: 768px) {
  .btn-clear {
    padding: 0.6rem 1.2rem;
    font-size: 1rem;
  }
}

.btn-clear:hover {
  background: #3b82f6;
  color: white;
  transform: scale(1.05);
}

@keyframes modalPop {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  z-index: 99999;
  animation: slideIn 0.3s ease;
  max-width: 90%;
  font-size: 14px;
}

@media (min-width: 768px) {
  .notification {
    padding: 15px 25px;
    font-size: 16px;
  }
}

.notification.success {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
}

.notification.error {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}
</style>